export type RoxyCommand =
  | { kind: 'startPublic' }
  | { kind: 'startParty' }
  | { kind: 'startPrivate' }
  | { kind: 'endPrivate' }
  | { kind: 'endShow' }
  | { kind: 'mute'; name: string }
  | { kind: 'ban'; name: string }
  | { kind: 'switchCamera' }
  | { kind: 'setPrivate'; coins: number }
  | { kind: 'thank'; name: string }
  | { kind: 'thankTopTipper' };

const WAKE_PREFIX = /^(?:hey\s+)?roxy[,.]?\s*/i;
const WAKE_ANYWHERE = /(?:hey\s+)?roxy[,.]?\s+/i;

export function stripRoxyWake(transcript: string): { armed: boolean; rest: string } {
  const trimmed = transcript.replace(/\s+/g, ' ').trim();
  if (!trimmed) {
    return { armed: false, rest: '' };
  }
  const prefix = trimmed.match(WAKE_PREFIX);
  if (prefix) {
    return { armed: true, rest: trimmed.slice(prefix[0].length).trim() };
  }
  const mid = trimmed.match(WAKE_ANYWHERE);
  if (mid && mid.index !== undefined) {
    return { armed: true, rest: trimmed.slice(mid.index + mid[0].length).trim() };
  }
  return { armed: false, rest: trimmed };
}

function normalizeCommandText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[?!.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function captureName(prefix: string, text: string): string | null {
  const match = text.match(new RegExp(`^${prefix}\\s+(.+)$`));
  const raw = match?.[1]?.trim();
  if (!raw) {
    return null;
  }
  return raw.replace(/^@/, '').replace(/\s+/g, ' ').trim();
}

export function parseRoxyCommand(transcript: string): RoxyCommand | null {
  const text = normalizeCommandText(transcript);
  if (!text) {
    return null;
  }

  if (
    /^(thank|thanks)\s+(the\s+)?top\s+tipper$/.test(text) ||
    /^thank\s+top\s+tipper$/.test(text)
  ) {
    return { kind: 'thankTopTipper' };
  }

  const thankName = captureName('(?:thank|thanks)', text);
  if (thankName) {
    return { kind: 'thank', name: thankName };
  }

  if (/^(switch|change|next)\s+cameras?$/.test(text) || text === 'switch camera') {
    return { kind: 'switchCamera' };
  }

  const setPrivate = text.match(
    /^(?:set\s+(?:private|vip|exclusive)(?:\s+to)?|private\s+to)\s+(\d+)\s*(?:coins?)?$/,
  );
  if (setPrivate?.[1]) {
    const coins = Number.parseInt(setPrivate[1], 10);
    if (Number.isFinite(coins) && coins > 0) {
      return { kind: 'setPrivate', coins };
    }
  }

  if (/^(end\s+show|go\s+offline|stop\s+broadcast(?:ing)?)$/.test(text)) {
    return { kind: 'endShow' };
  }

  if (
    /^(end\s+private|back\s+to\s+(?:public|main\s+stage)|resume\s+(?:public|main\s+stage))$/.test(
      text,
    )
  ) {
    return { kind: 'endPrivate' };
  }

  if (/^(start\s+private|go\s+private|start\s+(?:vip|exclusive))$/.test(text)) {
    return { kind: 'startPrivate' };
  }

  if (/^(start\s+party|go\s+party)$/.test(text)) {
    return { kind: 'startParty' };
  }

  if (
    /^(go\s+live|start(?:\s+public|\s+main\s+stage|\s+the\s+show)?)$/.test(text)
  ) {
    return { kind: 'startPublic' };
  }

  const muteName = captureName('mute', text);
  if (muteName) {
    return { kind: 'mute', name: muteName };
  }

  const banName = captureName('ban', text);
  if (banName) {
    return { kind: 'ban', name: banName };
  }

  return null;
}

export function isRoxyUtterance(transcript: string): boolean {
  return stripRoxyWake(transcript).armed;
}
